/**
 * Display formatting — the ONE place amounts, numbers, dates and times are
 * turned into text.
 *
 * Every monetary value arrives from the API as a decimal STRING. These helpers
 * format for display only, and never do arithmetic on the parsed number — the
 * server is the single authority on totals, and the browser must not
 * recalculate them into a slightly different answer.
 *
 * HOW THE ORGANIZATION'S SETTINGS GET HERE
 *
 * context/LocaleContext.jsx calls `configureFormatting()` with the active
 * organization's localization (currency, timezone, locale, date and time
 * format) and the person's own overrides (language, timezone). Every helper
 * below reads that configuration, so the ~900 existing call sites follow the
 * organization's settings without being touched, and new code gets them by
 * calling the same helpers. Nothing else in the app should build an
 * Intl.NumberFormat or Intl.DateTimeFormat for display.
 *
 * TIME ZONES
 *
 * Timestamps are UTC on the wire and in the database. They are shown in the
 * person's timezone if they chose one, else the organization's. A calendar
 * DATE (a due date, an invoice date) arrives as midnight UTC and names a day,
 * not an instant — it is shown as that day everywhere, never shifted, or every
 * due date would read a day early west of Greenwich.
 *
 * HISTORY
 *
 * `money(value, currency)` formats in the currency it is GIVEN. Pass the
 * document's own currency, so an old document keeps showing what it was
 * issued in after the organization changes its currency. The configured
 * currency is only the fallback for a value that has none.
 */

const DEFAULT_CONFIG = Object.freeze({
  /** Interface language: decides month names and the script. */
  language: 'en',
  /** BCP 47 locale for digit grouping and decimal marks (organization). */
  locale: 'en-IN',
  /** The zone times are SHOWN in; undefined = the browser's own. */
  timeZone: undefined,
  organizationTimeZone: undefined,
  userTimeZone: undefined,
  /**
   * Date pattern (DD/MM/YYYY ...). Null until an organization is open, which
   * keeps the original "02 Oct 2026" style on sign-in and public screens.
   */
  dateFormat: null,
  timeFormat: '24h',
  currency: 'INR',
  /** ISO weekday the week starts on: 1 = Monday ... 7 = Sunday. */
  firstDayOfWeek: 1,
});

let config = { ...DEFAULT_CONFIG };
let revision = 0;
const cache = new Map();

/**
 * Applies a new formatting configuration. Unknown keys are ignored and
 * missing ones keep their current value. Returns the revision, which changes
 * only when something that affects output actually changed.
 */
export function configureFormatting(next = {}) {
  const merged = { ...config };
  for (const key of Object.keys(DEFAULT_CONFIG)) {
    if (next[key] !== undefined) merged[key] = next[key];
  }
  if ('timeZone' in next) merged.timeZone = next.timeZone || undefined;
  if ('userTimeZone' in next) merged.userTimeZone = next.userTimeZone || undefined;

  const changed = Object.keys(DEFAULT_CONFIG).some((key) => merged[key] !== config[key]);
  if (changed) {
    config = merged;
    cache.clear();
    revision += 1;
  }
  return revision;
}

/** Back to the signed-out defaults (sign-out, leaving an organization). */
export function resetFormatting() {
  config = { ...DEFAULT_CONFIG };
  cache.clear();
  revision += 1;
  return revision;
}

/**
 * Runs `fn` with a different configuration and restores the live one before
 * returning — for previews ("this is how a date will look") on a settings
 * form, without touching what the rest of the app shows.
 */
export function withFormatting(overrides, fn) {
  const savedConfig = config;
  const savedCache = new Map(cache);
  config = { ...config, ...Object.fromEntries(Object.entries(overrides).filter(([, v]) => v !== undefined)) };
  cache.clear();
  try {
    return fn();
  } finally {
    config = savedConfig;
    cache.clear();
    for (const [key, value] of savedCache) cache.set(key, value);
  }
}

export const getFormattingConfig = () => config;
export const getFormattingRevision = () => revision;
export const getOrganizationTimezone = () => config.organizationTimeZone;
export const getUserTimezone = () => config.userTimeZone;
/** The zone times are shown in right now (person's, else organization's). */
export const getDisplayTimezone = () => config.timeZone;

/**
 * The locale numbers and dates are formatted in: the organization's locale
 * for its region (en-IN groups 1,25,000), in the interface language, always
 * with Latin digits so tables and inputs read the same in every language.
 */
export function formatLocale() {
  const key = 'locale';
  if (cache.has(key)) return cache.get(key);

  let tag = config.locale || 'en-IN';
  try {
    const base = new Intl.Locale(tag);
    if (base.language !== config.language) {
      tag = base.region ? `${config.language}-${base.region}` : config.language;
    }
    tag = `${tag}-u-nu-latn`;
    // Throws for a tag this browser cannot use; fall back to the language.
    new Intl.NumberFormat(tag);
  } catch {
    tag = `${config.language || 'en'}-u-nu-latn`;
  }
  cache.set(key, tag);
  return tag;
}

function memo(key, build) {
  if (!cache.has(key)) cache.set(key, build());
  return cache.get(key);
}

// --- money -------------------------------------------------------------------

function currencyFormatter(currency, locale) {
  return memo(`cur:${currency}:${locale}`, () => new Intl.NumberFormat(locale, { style: 'currency', currency }));
}

/**
 * "252725.00" -> "₹2,52,725.00" (INR, en-IN), "$1,250.00" (USD, en-US),
 * "AED 1,250.00" (AED, en-AE). Uses the currency's own minor units, so OMR,
 * KWD and BHD show three decimals.
 */
export function money(value, currency = undefined, locale = undefined) {
  if (value === null || value === undefined || value === '') return '—';
  const asNumber = Number(value);
  if (Number.isNaN(asNumber)) return String(value);

  const code = currency || config.currency || 'INR';
  try {
    return currencyFormatter(code, locale ?? formatLocale()).format(asNumber);
  } catch {
    // An unknown currency code should not blank out the number.
    return `${code} ${asNumber.toFixed(2)}`;
  }
}
export const formatCurrency = money;

/** The symbol a currency is written with in the current locale: ₹, $, AED. */
export function currencySymbol(currency = undefined) {
  const code = currency || config.currency || 'INR';
  try {
    const part = currencyFormatter(code, formatLocale())
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part?.value ?? code;
  } catch {
    return code;
  }
}

/** Compact form for dashboard tiles: 1.2L, 3.4Cr, 12.5K. */
export function moneyCompact(value, currency = undefined) {
  const asNumber = Number(value ?? 0);
  if (Number.isNaN(asNumber)) return '—';

  const code = currency || config.currency || 'INR';
  const symbol = currencySymbol(code);
  const abs = Math.abs(asNumber);

  // Indian numbering when the currency is rupees, since that is what a user
  // reading an INR figure expects.
  if (code === 'INR') {
    if (abs >= 1e7) return `${symbol}${(asNumber / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5) return `${symbol}${(asNumber / 1e5).toFixed(2)} L`;
    if (abs >= 1e3) return `${symbol}${(asNumber / 1e3).toFixed(1)}K`;
  } else {
    const spaced = /^[A-Z]{3}$/.test(symbol) ? `${symbol} ` : symbol;
    if (abs >= 1e9) return `${spaced}${(asNumber / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${spaced}${(asNumber / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${spaced}${(asNumber / 1e3).toFixed(1)}K`;
  }

  return money(value, code);
}

// --- numbers -----------------------------------------------------------------

/** "5.000" -> "5"; "2.500" -> "2.5". Trailing zeros are noise in a table. */
export function quantity(value) {
  if (value === null || value === undefined || value === '') return '—';
  const asNumber = Number(value);
  if (Number.isNaN(asNumber)) return String(value);
  return String(Number(asNumber.toFixed(3)));
}

export function number(value) {
  const asNumber = Number(value ?? 0);
  if (Number.isNaN(asNumber)) return '—';
  return memo(`num:${formatLocale()}`, () => new Intl.NumberFormat(formatLocale())).format(asNumber);
}
export const formatNumber = number;

export function percent(value) {
  if (value === null || value === undefined || value === '') return '0%';
  return `${Number(value)}%`;
}

// --- dates and times ---------------------------------------------------------

function toDate(value) {
  if (!value) return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * A calendar date rather than an instant: "2026-10-02", or exactly midnight
 * UTC, which is how DATE columns are serialised.
 */
export function isCalendarDate(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return true;
  const parsed = toDate(value);
  return Boolean(
    parsed &&
      parsed.getUTCHours() === 0 &&
      parsed.getUTCMinutes() === 0 &&
      parsed.getUTCSeconds() === 0 &&
      parsed.getUTCMilliseconds() === 0
  );
}

function partsIn(date, timeZone) {
  const formatter = memo(
    `parts:${timeZone ?? 'local'}`,
    () =>
      new Intl.DateTimeFormat('en-US', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      })
  );
  const parts = Object.fromEntries(
    formatter
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)])
  );
  return { ...parts, hour: parts.hour === 24 ? 0 : parts.hour };
}

/**
 * The wall-clock reading of an instant in the organization's zone:
 * { year, month, day, hour, minute, second }. Numbers to read, never a Date
 * to send back — what is stored stays UTC.
 */
export function convertToOrganizationTimezone(value) {
  const parsed = toDate(value);
  return parsed ? partsIn(parsed, config.organizationTimeZone) : null;
}

/** As above, in the zone the person chose (falling back to the organization's). */
export function convertToUserTimezone(value) {
  const parsed = toDate(value);
  return parsed ? partsIn(parsed, config.userTimeZone ?? config.organizationTimeZone) : null;
}

const pad = (n) => String(n).padStart(2, '0');

function monthShort(month) {
  const locale = formatLocale();
  return memo(`mon:${locale}:${month}`, () =>
    new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(2000, month - 1, 15)))
  );
}

const DATE_PATTERNS = {
  'DD/MM/YYYY': (p) => `${pad(p.day)}/${pad(p.month)}/${p.year}`,
  'MM/DD/YYYY': (p) => `${pad(p.month)}/${pad(p.day)}/${p.year}`,
  'YYYY-MM-DD': (p) => `${p.year}-${pad(p.month)}-${pad(p.day)}`,
  'DD-MMM-YYYY': (p) => `${pad(p.day)}-${monthShort(p.month)}-${p.year}`,
  'DD.MM.YYYY': (p) => `${pad(p.day)}.${pad(p.month)}.${p.year}`,
};
export const DATE_FORMATS = Object.freeze(Object.keys(DATE_PATTERNS));

function formatDay(parsed, { calendar }) {
  const zone = calendar ? 'UTC' : config.timeZone;
  if (config.dateFormat && DATE_PATTERNS[config.dateFormat]) {
    return DATE_PATTERNS[config.dateFormat](partsIn(parsed, zone));
  }
  // No organization open yet: the original "02 Oct 2026".
  return memo(
    `day:${formatLocale()}:${zone ?? 'local'}`,
    () => new Intl.DateTimeFormat(formatLocale(), { day: '2-digit', month: 'short', year: 'numeric', timeZone: zone })
  ).format(parsed);
}

/** "02/10/2026" in the organization's pattern. */
export function date(value) {
  const parsed = toDate(value);
  if (!parsed) return '—';
  return formatDay(parsed, { calendar: isCalendarDate(value) });
}
export const formatDate = date;

/** "02:30 PM" or "14:30", in the person's (else the organization's) zone. */
export function time(value) {
  const parsed = toDate(value);
  if (!parsed) return '—';
  const locale = formatLocale();
  return memo(
    `time:${locale}:${config.timeZone ?? 'local'}:${config.timeFormat}`,
    () =>
      new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: config.timeFormat === '12h' ? 'h12' : 'h23',
        timeZone: config.timeZone,
      })
  ).format(parsed);
}
export const formatTime = time;

/** "02/10/2026 14:30". Always an instant, so always converted. */
export function dateTime(value) {
  const parsed = toDate(value);
  if (!parsed) return '—';
  return `${formatDay(parsed, { calendar: false })} ${time(parsed)}`;
}
export const formatDateTime = dateTime;

/** For an <input type="date"> value. */
export function dateInput(value) {
  if (!value) return '';
  const parsed = toDate(value);
  if (!parsed) return '';
  if (isCalendarDate(value)) return parsed.toISOString().slice(0, 10);
  const p = partsIn(parsed, config.timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** "3 days ago", "in 2 weeks" — in the interface language, correctly pluralised. */
export function relativeTime(value) {
  const parsed = toDate(value);
  if (!parsed) return '—';

  const diffMs = parsed.getTime() - Date.now();
  const absMs = Math.abs(diffMs);
  const rtf = memo(`rtf:${config.language}`, () => new Intl.RelativeTimeFormat(config.language, { numeric: 'auto' }));

  const units = [
    ['year', 31_536_000_000],
    ['month', 2_592_000_000],
    ['week', 604_800_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  for (const [unit, ms] of units) {
    if (absMs >= ms) return rtf.format(Math.round(diffMs / ms), unit);
  }
  return rtf.format(0, 'second'); // "now" / "الآن"
}

/** True when a required-by or delivery date has passed. */
export function isOverdue(value) {
  if (!value) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.setHours(23, 59, 59, 999) < Date.now();
}

/** Month names in the interface language: ["January", ...] or ["يناير", ...]. */
export function monthNames(style = 'long') {
  const locale = formatLocale();
  return memo(`months:${locale}:${style}`, () =>
    Array.from({ length: 12 }, (_, index) =>
      new Intl.DateTimeFormat(locale, { month: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2000, index, 15)))
    )
  );
}

/** Weekday names, ISO order (Monday first): ["Monday", ...]. */
export function weekdayNames(style = 'long') {
  const locale = formatLocale();
  // 2024-01-01 was a Monday.
  return memo(`days:${locale}:${style}`, () =>
    Array.from({ length: 7 }, (_, index) =>
      new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, 1 + index)))
    )
  );
}

// --- text ------------------------------------------------------------------

/** "PURCHASE_MANAGER" -> "Purchase manager"; "PARTIALLY_RECEIVED" -> "Partially received" */
export function humanise(value) {
  if (!value) return '';
  const text = String(value).replace(/_/g, ' ').toLowerCase();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "purchase_order.approved" -> "Purchase order approved" */
export function humaniseAction(action) {
  if (!action) return '';
  return humanise(String(action).replace(/\./g, ' ').replace(/_/g, ' '));
}

export function initials(name, fallback = '?') {
  if (!name) return fallback;
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts.at(-1)[0]}`.toUpperCase();
}

export function pluralise(count, singular, plural = `${singular}s`) {
  return `${number(count)} ${Number(count) === 1 ? singular : plural}`;
}

export function truncate(value, max = 60) {
  const text = String(value ?? '');
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}
